import { UseInfiniteQueryResult } from '@tanstack/react-query'
import { Fragment, useMemo } from 'react'
import { InView } from 'react-intersection-observer'
import Alert from 'components/Alert'
import ItemLayout from 'components/ItemLayout'
import Loading from 'components/Loading'
import NfdResult from 'components/NfdResult'
import Filters from './Filters'
import { classNames } from 'helpers/utilities'
import { NfdRecord, NfdRecordCollection } from 'api/api-client'
import { InfiniteSearchQueryResult } from 'api/hooks/useInfiniteSearch'
import { ViewSetting } from './Controls'
import { ICustomError } from 'api/customError'

interface InfiniteResultsProps {
  query: UseInfiniteQueryResult<InfiniteSearchQueryResult, ICustomError>
  viewSetting: ViewSetting
  activeWallet: string | undefined
  search: string
  searchPlaceholder?: string
  viewId: string
  fetchedPages?: Set<number>
  refetchPage?: (page: number) => void
  context?: 'browse' | 'manage'
}

export default function InfiniteResults({
  query,
  viewSetting,
  activeWallet,
  search,
  searchPlaceholder = 'Search',
  viewId,
  fetchedPages,
  refetchPage,
  context = 'browse'
}: InfiniteResultsProps) {
  const { data, error, isFetchingNextPage, isInitialLoading, fetchNextPage, hasNextPage } = query

  const allResults = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.nfds).filter((nfd) => nfd !== null) || []
  }, [data])

  const hasFilter = search !== ''

  const renderResult = (result: NfdRecord) => {
    return (
      <NfdResult
        key={result.name}
        view={viewSetting}
        nfd={result}
        activeWallet={activeWallet}
        context={context}
      />
    )
  }

  const renderResults = (results: NfdRecordCollection | null, pageIdx: number) => {
    if (!data || !results) {
      return null
    }

    return results.map((result: NfdRecord, i) => {
      const handleInViewChange = (inView: boolean) => {
        if (!inView) return

        const isLastPage = pageIdx === data.pages.length - 1
        const needsRefresh = refetchPage && !fetchedPages?.has(pageIdx)

        if (isLastPage && hasNextPage) {
          fetchNextPage()
        } else if (needsRefresh) {
          refetchPage(pageIdx)
        }
      }

      const isLastResult = i === results.length - 1

      if (isLastResult) {
        return (
          <InView key={result.name} as="div" onChange={handleInViewChange}>
            {renderResult(result)}
          </InView>
        )
      }

      return renderResult(result)
    })
  }

  const isList = viewSetting === 'list'
  const filtersClassName = isList ? 'sm:-mt-6 sm:mb-6' : '-mt-6 mb-6'

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loading />
        </div>
      )
    }

    if (error) {
      return (
        <div className="py-5">
          <Alert type="error" title="Error" message="An error occurred while fetching records." />
        </div>
      )
    }

    if (allResults.length === 0) {
      const message = hasFilter ? 'No records found matching search text' : 'No records found'

      return (
        <div className="py-5">
          <Alert type="info" title={message} />
        </div>
      )
    }

    return (
      <ItemLayout view={viewSetting} className={classNames(isList ? '-mx-4 sm:m-0' : '')}>
        {data?.pages.map((page, pageIdx) => (
          <Fragment key={pageIdx}>{renderResults(page.data.nfds, pageIdx)}</Fragment>
        ))}
      </ItemLayout>
    )
  }

  return (
    <>
      <div>
        <Filters
          key={viewId}
          search={search}
          placeholder={searchPlaceholder}
          className={filtersClassName}
        />
        {renderContent()}
      </div>

      {isFetchingNextPage && (
        <div className="flex flex-col items-center justify-center py-32">
          <Loading />
        </div>
      )}
    </>
  )
}
